import { collectResourceConditionalRequirements } from "#bb/lib/conditionalRules";
import { resolveResourcePath } from "#bb/lib/pathContract";

interface CandidateResourceLoadResult {
  requestedPath: string;
  definitionPath: string;
  contentType: string;
  raw: string;
  metadata: Record<string, unknown> | null;
  hasConditionalAnswers: boolean;
  redirectHref?: string;
}

interface LoadOptions {
  refreshDependencies?: boolean;
  refreshDefinition?: boolean;
}

const inFlightByApp = new WeakMap<object, Map<string, Promise<unknown>>>();

function normalizeResourcePath(value: string) {
  const url = new URL(value, "http://localhost");
  if (url.origin !== "http://localhost") return null;

  return url.pathname.replace(/^\/+|\/+$/g, "");
}

function getIndexPath(path: string) {
  return path ? `${path}/index` : "index";
}

function latestVersionId(
  versions: Array<{ id: string; version: number }> | undefined,
) {
  if (!versions?.length) return undefined;
  return versions.reduce((latest, current) =>
    current.version > latest.version ? current : latest,
  ).id;
}

export function useCandidateResourceLoader() {
  const nuxtApp = useNuxtApp();
  const logger = useLogger("CandidateResourceLoader");
  const router = useRouter();
  const { site } = useSite();
  const { profile } = useProfile();
  const { fetchAnswers } = useAnswers();
  const { isResourceDefinitionType } = useResourceDefinitions();
  const { hasPermission, hasContentPermission, isInternalContentType } =
    usePermissions();

  const loadedMap = useState<Record<string, boolean>>(
    "resource-definition-loaded-map",
    () => ({}),
  );
  const readyMap = useState<Record<string, boolean>>(
    "resource-definition-ready-map",
    () => ({}),
  );
  const definitionPathMap = useState<Record<string, string>>(
    "resource-definition-path-map",
    () => ({}),
  );
  const rawMap = useState<Record<string, string>>(
    "resource-definition-raw-map",
    () => ({}),
  );
  const metadataMap = useState<Record<string, Record<string, unknown> | null>>(
    "resource-definition-metadata-map",
    () => ({}),
  );
  const contentTypeMap = useState<Record<string, string>>(
    "resource-definition-content-type-cache",
    () => ({}),
  );
  const conditionalAnswersMap = useState<Record<string, boolean>>(
    "resource-definition-conditional-answers-map",
    () => ({}),
  );
  const redirectMap = useState<Record<string, string>>(
    "resource-definition-redirect-map",
    () => ({}),
  );

  let inFlight = inFlightByApp.get(nuxtApp as object);
  if (!inFlight) {
    inFlight = new Map();
    inFlightByApp.set(nuxtApp as object, inFlight);
  }

  function canReadContentType(type?: string | null) {
    if (!type) return false;
    if (
      isInternalContentType(type) &&
      !hasPermission(profile.value?.permissions, "internal:admin")
    ) {
      return false;
    }

    return hasContentPermission(profile.value?.permissions, type, "read");
  }

  function cachedResult(path: string): CandidateResourceLoadResult {
    return {
      requestedPath: path,
      definitionPath: definitionPathMap.value[path] ?? path,
      contentType: contentTypeMap.value[path] ?? "",
      raw: rawMap.value[path] ?? "",
      metadata: metadataMap.value[path] ?? null,
      hasConditionalAnswers: conditionalAnswersMap.value[path] ?? false,
      redirectHref: redirectMap.value[path],
    };
  }

  async function dedupe<T>(key: string, operation: () => Promise<T>) {
    const existing = inFlight.get(key) as Promise<T> | undefined;
    if (existing) return existing;

    const promise = operation().finally(() => {
      inFlight.delete(key);
    });
    inFlight.set(key, promise);
    return promise;
  }

  async function downloadDefinition(
    siteId: string,
    fileEntry: {
      path: string;
      versions?: Array<{ id: string; version: number }>;
    },
  ) {
    const versionId = latestVersionId(fileEntry.versions);
    const versionQuery = versionId ? `?version=${versionId}` : "";
    return useApiFetch<string>(
      `/v1/file/${siteId}/download/${fileEntry.path}${versionQuery}`,
      { responseType: "text" },
    );
  }

  async function loadDefinition(
    requestedPath: string,
    redirects = new Set<string>(),
    refresh = false,
  ): Promise<CandidateResourceLoadResult> {
    if (redirects.has(requestedPath)) {
      throw new Error(`Circular resource redirect detected at '${requestedPath}'.`);
    }
    if (loadedMap.value[requestedPath] && !refresh) {
      return cachedResult(requestedPath);
    }

    const operation = async () => {
      const siteId = site.value?.id;
      if (!siteId) {
        throw new Error("A site ID is required to load a resource definition.");
      }

      const { getEntry } = useFileEntries(siteId);
      let fileEntry = await getEntry(requestedPath, "metadata", {
        suppressNotFoundToast: true,
      });

      if (fileEntry?.type === "application/vnd.pivotalgroup.redirect+json") {
        if (!canReadContentType(fileEntry.type)) {
          throw createError({
            statusCode: 403,
            statusMessage: "Resource Access Denied",
            fatal: true,
            data: { resource: requestedPath },
          });
        }

        redirects.add(requestedPath);

        const rawRedirect = await downloadDefinition(siteId, fileEntry);
        const redirect = JSON.parse(rawRedirect);
        const redirectPath =
          typeof redirect?.path === "string"
            ? normalizeResourcePath(resolveResourcePath(
                redirect.path,
                fileEntry.path,
              ))
            : null;
        if (!redirectPath) {
          throw new Error("Resource redirect does not contain a valid internal path.");
        }

        const target = await loadDefinition(redirectPath, redirects, refresh);
        const terminalHref = target.redirectHref ?? `/${redirectPath}`;
        loadedMap.value[requestedPath] = true;
        redirectMap.value[requestedPath] = terminalHref;
        return {
          ...target,
          requestedPath,
          redirectHref: terminalHref,
        };
      }

      if (!fileEntry || !isResourceDefinitionType(fileEntry.type)) {
        const indexPath = getIndexPath(requestedPath);
        fileEntry = await getEntry(indexPath, "metadata", {
          suppressNotFoundToast: true,
        });
        if (!fileEntry) throw new Error("File not found");
      }

      if (!canReadContentType(fileEntry.type)) {
        throw createError({
          statusCode: 403,
          statusMessage: "Resource Access Denied",
          fatal: true,
          data: { resource: requestedPath },
        });
      }

      const raw = await downloadDefinition(siteId, fileEntry);
      if (!raw) throw new Error("Resource definition is empty.");

      const parsed = JSON.parse(raw);
      const content = Array.isArray(parsed.content) ? parsed.content : [];
      const metadata =
        parsed.metadata && typeof parsed.metadata === "object"
          ? parsed.metadata as Record<string, unknown>
          : null;
      const manager = useContentManager(requestedPath);
      manager.setContentType(fileEntry.type);
      manager.setContent(content);
      const requirements = collectResourceConditionalRequirements(
        manager.contentState.value,
      );

      definitionPathMap.value[requestedPath] = fileEntry.path;
      rawMap.value[requestedPath] = raw;
      metadataMap.value[requestedPath] = metadata;
      contentTypeMap.value[requestedPath] = fileEntry.type;
      delete redirectMap.value[requestedPath];
      conditionalAnswersMap.value[requestedPath] =
        requirements.answers.length > 0;
      loadedMap.value[requestedPath] = true;

      return cachedResult(requestedPath);
    };

    return redirects.size > 0
      ? operation()
      : dedupe(
          `definition:${requestedPath}:${refresh ? "refresh" : "cached"}`,
          operation,
        );
  }

  async function prepareDependencies(
    result: CandidateResourceLoadResult,
    refresh = false,
  ) {
    const path = result.redirectHref
      ? normalizeResourcePath(result.redirectHref)
      : result.requestedPath;
    if (!path) return;
    const dependencyKey = `dependencies:${path}`;
    const existing = inFlight.get(dependencyKey);
    if (existing) {
      await existing;
    }
    if (readyMap.value[path] && !refresh) return;
    if (refresh) {
      readyMap.value[path] = false;
    }

    await dedupe(dependencyKey, async () => {
      const manager = useContentManager(path);
      const requirements = collectResourceConditionalRequirements(
        manager.contentState.value,
      );
      const { load: loadConditionalUserData } = useConditionalUserData(path);

      await Promise.all([
        requirements.answers.length > 0
          ? fetchAnswers({ force: true })
          : Promise.resolve(),
        loadConditionalUserData(requirements),
      ]);
      readyMap.value[path] = true;
    });
  }

  async function load(path: string, options: LoadOptions = {}) {
    const normalizedPath = normalizeResourcePath(path);
    if (normalizedPath === null) {
      throw new Error("External destinations cannot be loaded as resources.");
    }

    const result = await loadDefinition(
      normalizedPath,
      new Set<string>(),
      options.refreshDefinition,
    );
    await prepareDependencies(
      result,
      options.refreshDependencies || options.refreshDefinition,
    );
    if (result.redirectHref) {
      readyMap.value[normalizedPath] = true;
    }
    return result;
  }

  async function preload(href: string, options: LoadOptions = {}) {
    const path = normalizeResourcePath(href);
    if (path === null) return false;

    try {
      await load(path, options);
      return true;
    } catch (error) {
      logger.warn("Unable to preload candidate resource", { href, error });
      return false;
    }
  }

  function isReady(href: string) {
    const path = normalizeResourcePath(href);
    if (path === null) return false;
    return loadedMap.value[path] === true && readyMap.value[path] === true;
  }

  async function activateRedirect(result: CandidateResourceLoadResult) {
    if (result.redirectHref) {
      await router.replace(result.redirectHref);
      return true;
    }
    return false;
  }

  return {
    load,
    preload,
    isReady,
    activateRedirect,
  };
}
